import { useMarketplaceItems } from '@/apis/marketplace/hooks/useMarketplaceItems'
import { MarketplaceItem } from '@/apis/marketplace/marketplace.schema'
import { useApplicantProgramYears } from '@/app/SchoolYearProvider'
import { usePagination } from '@/common/hooks/usePagination.tsx'
import {
  useMarketplaceStores,
  useMarketplaceAffiliations,
} from '@/queries/marketplace/hooks.ts'
import { AppRoutePaths } from '@/routing/route_paths'
import { selectProgramSettings } from '@/storage/program_settings/programSettingsSlice'
import { useDebounce } from '@uidotdev/usehooks'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import MarketplaceUI from '../components/MarketplaceUI'
import {
  MarketplaceAffiliation,
  MarketplaceStore,
} from '@/queries/marketplace/schema'
import {
  MARKETPLACE_AVAILABILITY_FILTERS,
  TABLE_FILTERS,
} from '@/app/pages/marketplace/constants'
import {
  resolveStoreScope,
  StoreScope,
  StoreScopeFlags,
} from '@/app/pages/marketplace/utils'

type MarketplaceView = 'stores' | 'items' | 'affiliations'

export default function MarketplacePage() {
  const navigate = useNavigate()

  const [showCart, setShowCart] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedFilter, setSelectedFilter] = useState<string>('')
  const [selectedAvailability, setSelectedAvailability] = useState<
    number | undefined
  >(undefined)
  const [currentView, setCurrentView] = useState<MarketplaceView>('stores')
  // Kept in component state on purpose: every visit starts on "My Schools".
  const [storeScope, setStoreScope] = useState<StoreScope>('my_schools')
  const [storeScopeFlags, setStoreScopeFlags] =
    useState<StoreScopeFlags | null>(null)

  const debouncedValue = useDebounce(search, 500)
  const { selectedProgramId } = useApplicantProgramYears()
  const { currentPage, perPage, setCurrentPage, setPerPage } = usePagination()

  const { requestMySchools, showScopeToggle, showAddStudentPrompt } =
    resolveStoreScope({ selectedScope: storeScope, flags: storeScopeFlags })

  const { data: storesData, isLoading: isStoresLoading } = useMarketplaceStores(
    selectedProgramId,
    selectedFilter,
    debouncedValue,
    selectedAvailability,
    perPage,
    currentPage,
    requestMySchools,
    {
      enabled: currentView === 'stores' && selectedProgramId != null,
    }
  )

  const reportedHasStudents = storesData?.has_students
  const reportedHasSchoolLinks = storesData?.has_school_links

  // Students and their schools are per program, so a program switch starts over
  // on the first page of "My Schools" instead of keeping the previous program's
  // page, choice and flags. Declared before the effect below, which re-reads the
  // flags on a program switch, so a cached response for the new program restores
  // them straight away.
  useEffect(() => {
    setStoreScope('my_schools')
    setStoreScopeFlags(null)
    setCurrentPage(1)
  }, [selectedProgramId, setCurrentPage])

  useEffect(() => {
    if (
      reportedHasStudents === undefined ||
      reportedHasSchoolLinks === undefined
    )
      return

    setStoreScopeFlags((previous) =>
      previous?.hasStudents === reportedHasStudents &&
      previous?.hasSchoolLinks === reportedHasSchoolLinks
        ? previous
        : {
            hasStudents: reportedHasStudents,
            hasSchoolLinks: reportedHasSchoolLinks,
          }
    )
  }, [reportedHasStudents, reportedHasSchoolLinks, selectedProgramId])

  // The scoped response has just reported there's nothing to scope to; the
  // effect above is about to switch to every store, so don't flash an empty list.
  const isFallingBackToAllStores =
    requestMySchools && reportedHasSchoolLinks === false

  const { data: affiliationsData, isLoading: isAffiliationsLoading } =
    useMarketplaceAffiliations(
      selectedProgramId,
      debouncedValue,
      selectedAvailability,
      perPage,
      currentPage,
      {
        enabled: currentView === 'affiliations' && selectedProgramId != null,
      }
    )

  const { data: itemsRecords, isLoading: isItemsLoading } = useMarketplaceItems(
    selectedProgramId,
    selectedFilter,
    debouncedValue,
    undefined,
    undefined,
    selectedAvailability,
    perPage,
    currentPage,
    {
      enabled: currentView === 'items' && selectedProgramId != null,
    }
  )

  // Computed values
  const stores = isFallingBackToAllStores ? [] : (storesData?.data ?? [])
  const affiliations = affiliationsData?.data ?? []
  const items = itemsRecords?.data ?? []

  const totalRecords =
    currentView === 'stores'
      ? isFallingBackToAllStores
        ? 0
        : (storesData?.total ?? 0)
      : currentView === 'affiliations'
        ? (affiliationsData?.total ?? 0)
        : (itemsRecords?.total ?? 0)

  const isLoading =
    currentView === 'stores'
      ? isStoresLoading || isFallingBackToAllStores
      : currentView === 'affiliations'
        ? isAffiliationsLoading
        : isItemsLoading

  const programSettings = useSelector(selectProgramSettings)
  const pathname = useLocation().pathname

  useEffect(() => {
    if (programSettings.marketplace_module == 0) navigate(-1)
    else if (pathname.includes(AppRoutePaths.marketplace.cart.index))
      setShowCart(true)
  }, [])

  const handleShopClick = (item: MarketplaceItem) => {
    navigate(
      AppRoutePaths.marketplace.details.index.replace(
        ':itemId',
        item.id.toString()
      )
    )
  }

  const hideCart = () => {
    navigate(AppRoutePaths.marketplace.index)
    setShowCart(false)
  }

  const handleFilterOnChange = (item: { id: number; name: string }) => {
    setSelectedFilter(
      item.id == 0 ? '' : item.id == 1 ? '1,2' : item.id.toString()
    )
    setCurrentPage(1)
  }

  const handleAvailabilityFilterOnChange = (item: {
    id: number
    name: string
  }) => {
    setSelectedAvailability(item.id === 0 ? undefined : item.id === 1 ? 1 : 0)
    setCurrentPage(1)
  }

  const handleStoreScopeChange = (scope: StoreScope) => {
    setStoreScope(scope)
    setCurrentPage(1)
  }

  const handleViewChange = (view: MarketplaceView) => {
    setCurrentView(view)
    setSearch('')
    setSelectedFilter('')
    setCurrentPage(1)
  }

  const handleProviderClick = (providerId: number) => {
    navigate(
      AppRoutePaths.marketplace.provider.index.replace(
        ':providerId',
        providerId.toString()
      )
    )
  }

  function handleStoreClick(store: MarketplaceStore) {
    navigate(
      AppRoutePaths.marketplace.provider.index.replace(
        ':providerId',
        store.id.toString()
      )
    )
  }

  const handleAffiliationClick = (affiliation: MarketplaceAffiliation) => {
    navigate(
      AppRoutePaths.marketplace.affiliation.index.replace(
        ':affiliationId',
        affiliation.id.toString()
      )
    )
  }

  return (
    <MarketplaceUI
      stores={stores}
      affiliations={affiliations}
      items={items}
      totalRecords={totalRecords}
      showCart={showCart}
      search={search}
      isLoading={isLoading}
      storeScope={storeScope}
      showStoreScopeToggle={showScopeToggle}
      showAddStudentPrompt={showAddStudentPrompt}
      onStoreScopeChange={handleStoreScopeChange}
      handleStoreClick={handleStoreClick}
      currentView={currentView}
      currentPage={currentPage}
      perPage={perPage}
      filters={TABLE_FILTERS}
      onSearchChange={setSearch}
      onViewChange={handleViewChange}
      onFilterChange={handleFilterOnChange}
      onItemClick={handleShopClick}
      onAffiliationClick={handleAffiliationClick}
      onProviderClick={handleProviderClick}
      onPageChange={setCurrentPage}
      onPerPageChange={setPerPage}
      hideCart={hideCart}
      availabilityFilters={MARKETPLACE_AVAILABILITY_FILTERS}
      onAvailabilityFilterChange={handleAvailabilityFilterOnChange}
    />
  )
}
